import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ReactComponent as RocketIcon } from '../../assets/icons/rocket.svg';
import { ReactComponent as MonitorWaveformIcon } from '../../assets/icons/monitor-waveform.svg';
import { ReactComponent as ShieldKeyholeIcon } from '../../assets/icons/shield-keyhole.svg';
import type { OnboardingTourStep } from '../ui/OnboardingTourModal';

// The tour body lives in the UI-library modal. It is lazy-loaded so the App-level
// launcher adds only this tiny gate to the main bundle; the split-layout modal
// (GritModalShell, GritButton, tokens) is fetched on demand the first time the
// tour is actually shown.
const load = () => import('../ui/OnboardingTourModal');
const OnboardingTourModal = React.lazy(load);

// Route prefixes on which the first-run tour must not appear. This is a verbatim
// copy of GritClawWrapper's `blockedPrefixes` list (App.tsx) — the two lists are
// intentionally duplicated (each gates a different app-wide singleton) and must
// be kept in sync by hand. See the CONCERNS note in the wiring report.
const BLOCKED_PREFIXES = [
  '/gritclaw',
  '/login',
  '/forgot-password',
  '/reset-password',
  '/create-password',
  '/accept-t&c',
  '/profile-selection',
  '/privacy-policy',
  '/unsubscribe/',
];

/** Per-user, per-browser localStorage key marking the tour as already seen. */
const seenKey = (email: string | undefined): string =>
  `gc-onboarding-tour-seen:${email ?? ''}`;

/**
 * Read the seen flag. Guarded in try/catch (private-mode / disabled / quota) so a
 * storage failure never throws inside an App-level render — the ZoomModal
 * precedent reads localStorage unguarded, but this mounts app-wide so we go
 * defensive: on any error we treat the tour as unseen and simply let it show.
 */
const readSeen = (email: string | undefined): boolean => {
  try {
    return localStorage.getItem(seenKey(email)) === 'true';
  } catch {
    return false;
  }
};

/** Persist the seen flag. Guarded so a storage failure is a no-op, never a throw. */
const writeSeen = (email: string | undefined): void => {
  try {
    localStorage.setItem(seenKey(email), 'true');
  } catch {
    // Storage unavailable (private mode / quota / disabled) — nothing to persist.
  }
};

interface TourCookieUser {
  email?: string;
  [key: string]: unknown;
}

/**
 * OnboardingTourLauncher — App-level first-run gate for the "Meet GritClaw.ai"
 * tour. Mounts the lazy OnboardingTourModal exactly once per browser for an
 * authenticated Customer user who has not yet seen (or dismissed) it. Every
 * dismissal path — Skip, ✕, Escape, backdrop, and Get started — marks the tour
 * seen so it can never reappear.
 */
const OnboardingTourLauncher: React.FC = () => {
  const { t } = useTranslation();
  const [cookies] = useCookies(['user', 'token', 'super_user']);
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const user = cookies.user as TourCookieUser | undefined;
  const email = user?.email;
  const token = cookies.token as string | undefined;

  const blocked = BLOCKED_PREFIXES.some((p) => location.pathname.startsWith(p));
  const isAdmin = location.pathname.startsWith('/admin');
  const isReseller = location.pathname.startsWith('/reseller');
  const isSuperUser = !!cookies.super_user;

  // Show only for an authenticated, non-super Customer user on a normal app page.
  const eligible =
    !!user && !!token && !blocked && !isAdmin && !isReseller && !isSuperUser;

  useEffect(() => {
    if (!eligible || readSeen(email)) {
      setOpen(false);
      return;
    }
    // Defer opening to the next frame so the tour never competes with the initial
    // route paint (the modal is lazy-loaded, so the heavy render is deferred too).
    let raf = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (typeof window.requestAnimationFrame === 'function') {
      raf = window.requestAnimationFrame(() => setOpen(true));
    } else {
      timer = setTimeout(() => setOpen(true), 0);
    }
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      if (timer) clearTimeout(timer);
    };
  }, [eligible, email]);

  // Skip / ✕ / Escape / backdrop / Get started all converge here: mark the tour
  // seen (idempotent — the modal fires onComplete then onClose) and close it.
  const markSeenAndClose = useCallback(() => {
    writeSeen(email);
    setOpen(false);
  }, [email]);

  const steps: OnboardingTourStep[] = useMemo(
    () => [
      {
        Icon: RocketIcon,
        title: t('Meet GritClaw.ai'),
        body: t(
          'Logistics doesn’t need another dashboard — it needs an autonomous assistant with eyes and ears on the ground. GritClaw watches every asset for you, around the clock.'
        ),
      },
      {
        Icon: MonitorWaveformIcon,
        title: t('Every alert, actionable'),
        body: t(
          'When a seal opens or a reefer drifts off-temperature, GritClaw surfaces the exception with the context to act — and can hold cargo at the gate on your say-so.'
        ),
      },
      {
        Icon: ShieldKeyholeIcon,
        title: t('Deploy. Detect. Direct.'),
        body: t(
          'Plug-and-play iM- hardware arms in seconds. Register your first device and GritClaw takes it from there. Confidence in every movement.'
        ),
      },
    ],
    [t]
  );

  if (!open) return null;

  return (
    <Suspense fallback={null}>
      <OnboardingTourModal
        open
        onClose={markSeenAndClose}
        onComplete={markSeenAndClose}
        steps={steps}
        skipLabel={t('Skip tour')}
        backLabel={t('Back')}
        nextLabel={t('Next')}
        completeLabel={t('Get started')}
      />
    </Suspense>
  );
};

export default OnboardingTourLauncher;
